/**
 * Authentication context: token + user state, login/logout, JWT persistence.
 *
 * In mock mode the backend issues a demo JWT whose claims include the selected
 * persona's Entra group membership. The token is persisted in localStorage so a
 * page refresh keeps the session. `user.entra_groups` drives the permission-filter
 * UI throughout the app.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api } from '../lib/api';
import { AUTH_TOKEN_KEY } from '../lib/config';
import { endSsoSession, ssoEnabled } from '../lib/sso';
import type { PersonaKey } from '../lib/constants';

export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  /** Human-readable role label (e.g. "Finance Analyst") — the only identity shown in the UI. */
  role: string;
  /** Entra group claims — the authorization primitive for the permission filter. */
  entraGroups: string[];
  /** Demo persona key, when authenticated via the mock persona switcher. */
  personaKey?: PersonaKey;
}

interface JwtPayload {
  sub?: string;
  user_id?: string;
  email?: string;
  name?: string;
  display_name?: string;
  role?: string;
  entra_groups?: string[];
  persona_key?: PersonaKey;
  exp?: number;
}

interface LoginResponse {
  token: string;
  token_type?: string;
}

export interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /**
   * Sign in with the real credential (email + password). This is the only gate:
   * there is no bundled password and no auto-fill.
   */
  login: (email: string, password: string) => Promise<void>;
  /** Adopt an externally obtained JWT (Cognito SSO callback). */
  loginWithToken: (token: string) => void;
  /**
   * Switch the active role *view* (e.g. Finance, HR, Admin/all) for the current
   * authenticated session — no password. Powered by `POST /auth/assume-role`.
   */
  assumeRole: (roleKey: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function decodeJwtPayload(token: string): JwtPayload | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    return JSON.parse(json) as JwtPayload;
  } catch {
    return null;
  }
}

function isExpired(payload: JwtPayload): boolean {
  if (!payload.exp) return false;
  return Date.now() >= payload.exp * 1000;
}

function toUser(payload: JwtPayload): AuthUser | null {
  const id = payload.user_id ?? payload.sub;
  const email = payload.email;
  if (!id || !email) return null;
  return {
    id,
    email,
    displayName: payload.display_name ?? payload.name ?? email,
    role: payload.role ?? 'viewer',
    entraGroups: payload.entra_groups ?? [],
    ...(payload.persona_key ? { personaKey: payload.persona_key } : {}),
  };
}

/** Roles that unlock the admin surfaces (Dashboard, Audit, Retrieval…). */
export function isAdminRole(role: string | undefined): boolean {
  return role === 'Platform Administrator' || role === 'Executive Leadership';
}

function readStoredToken(): string | null {
  try {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Cognito tokens carry no role claim — group→role mapping is server-side.
  // Hydrate from /auth/me so the UI can gate admin surfaces.
  useEffect(() => {
    if (!token || !ssoEnabled()) return;
    let cancelled = false;
    api
      .get<{ email: string; role: string; entra_groups: string[] }>('/auth/me')
      .then((me) => {
        if (cancelled) return;
        setUser((u) =>
          u ? { ...u, role: me.role, entraGroups: me.entra_groups } : u,
        );
      })
      .catch(() => {
        /* keep the decoded-token fallback */
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    const stored = readStoredToken();
    if (stored) {
      const payload = decodeJwtPayload(stored);
      if (payload && !isExpired(payload)) {
        const decoded = toUser(payload);
        if (decoded) {
          setToken(stored);
          setUser(decoded);
        }
      }
      if (!payload || isExpired(payload) || !toUser(payload)) {
        try {
          localStorage.removeItem(AUTH_TOKEN_KEY);
        } catch {
          /* ignore */
        }
      }
    }
    setIsLoading(false);
  }, []);

  // Decode a freshly issued JWT, hydrate the user, and persist the session.
  const applyToken = useCallback((newToken: string): void => {
    const payload = decodeJwtPayload(newToken);
    const decoded = payload ? toUser(payload) : null;
    if (!decoded) {
      throw new Error('Invalid token received from server');
    }
    try {
      localStorage.setItem(AUTH_TOKEN_KEY, newToken);
    } catch {
      /* ignore storage errors; session stays in-memory */
    }
    setToken(newToken);
    setUser(decoded);
  }, []);

  const loginWithToken = useCallback(
    (newToken: string): void => {
      applyToken(newToken);
    },
    [applyToken],
  );

  const login = useCallback(
    async (email: string, password: string): Promise<void> => {
      const res = await api.post<LoginResponse>(
        '/auth/login',
        { persona_key: email, password },
        { skipAuth: true },
      );
      applyToken(res.token);
    },
    [applyToken],
  );

  // Switch role view for the current session — sends the bearer token (no
  // skipAuth), so the server gates it on being authenticated, not a password.
  const assumeRole = useCallback(
    async (roleKey: string): Promise<void> => {
      const res = await api.post<LoginResponse>('/auth/assume-role', {
        role_key: roleKey,
      });
      applyToken(res.token);
    },
    [applyToken],
  );

  const logout = useCallback((): void => {
    try {
      localStorage.removeItem(AUTH_TOKEN_KEY);
    } catch {
      /* ignore */
    }
    setToken(null);
    setUser(null);
    if (ssoEnabled()) {
      endSsoSession();
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      token,
      isAuthenticated: Boolean(user && token),
      isLoading,
      login,
      loginWithToken,
      assumeRole,
      logout,
    }),
    [user, token, isLoading, login, assumeRole, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
